import { useEffect, useState } from "react";
import { FaEdit, FaSort, FaSortDown, FaSortUp, FaTrash } from "react-icons/fa";
import { fetchWithAuth } from "../api/fetchWithAuth";
import DeviceFormModal from "../components/DeviceFormModal";
import DeviceModal from "../components/DeviceModal";

export default function Devices({ user }) {
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortField, setSortField] = useState("name");
  const [sortDir, setSortDir] = useState("asc");

  const roleCodes = user?.roles ?? [];
  const canManage = roleCodes.includes("PM") || Boolean(user?.isAdmin);

  useEffect(() => {
    loadDevices();
  }, []);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(search);
    }, 180);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [search]);

  const loadDevices = async () => {
    try {
      const res = await fetchWithAuth("/api/devices");

      if (!res.ok) {
        console.error("Failed to load devices");
        return;
      }

      setDevices(await res.json());
    } catch (err) {
      console.warn("loadDevices aborted:", err.message);
    }
  };

  const openDevice = async (id) => {
    const res = await fetchWithAuth(`/api/devices/${id}`);

    if (!res.ok) return;

    setSelectedDevice(await res.json());
    setShowModal(true);
  };

  const deleteDevice = async (id) => {
    if (!window.confirm("Delete this device?")) return;

    const res = await fetchWithAuth(`/api/devices/${id}`, {
      method: "DELETE"
    });

    if (!res.ok) {
      const error = await res.json().catch(() => null);
      alert(error?.error || "Delete failed");
      return;
    }

    loadDevices();
  };

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const filteredDevices = devices.filter((device) => {
    const query = debouncedSearch.trim().toLowerCase();
    if (!query) return true;

    return (
      String(device.name ?? "").toLowerCase().includes(query) ||
      String(device.note ?? "").toLowerCase().includes(query) ||
      String(device.User?.name ?? "").toLowerCase().includes(query)
    );
  });

  const sortedDevices = [...filteredDevices].sort((a, b) => {
    const direction = sortDir === "asc" ? 1 : -1;
    const valueA = String(sortField === "user" ? a.User?.name ?? "" : a[sortField] ?? "").toLowerCase();
    const valueB = String(sortField === "user" ? b.User?.name ?? "" : b[sortField] ?? "").toLowerCase();

    const compare = valueA.localeCompare(valueB, undefined, {
      numeric: true,
      sensitivity: "base"
    });

    if (compare !== 0) return compare * direction;

    return String(a.name ?? "").localeCompare(
      String(b.name ?? ""),
      undefined,
      { numeric: true, sensitivity: "base" }
    );
  });

  const renderHighlightedText = (value) => {
    const text = String(value ?? "");
    const query = debouncedSearch.trim();

    if (!query) return text;

    const lowerText = text.toLowerCase();
    const lowerQuery = query.toLowerCase();
    const parts = [];
    let startIndex = 0;
    let matchIndex = lowerText.indexOf(lowerQuery);

    while (matchIndex !== -1) {
      if (matchIndex > startIndex) {
        parts.push(text.slice(startIndex, matchIndex));
      }

      const endIndex = matchIndex + query.length;
      parts.push(
        <mark key={`${text}-${matchIndex}`} className="px-0 bg-warning-subtle">
          {text.slice(matchIndex, endIndex)}
        </mark>
      );

      startIndex = endIndex;
      matchIndex = lowerText.indexOf(lowerQuery, startIndex);
    }

    if (startIndex < text.length) {
      parts.push(text.slice(startIndex));
    }

    return parts.length ? parts : text;
  };

  const renderSortIndicator = (field) => {
    if (sortField !== field) {
      return <FaSort className="ms-1 text-muted" aria-hidden="true" />;
    }

    if (sortDir === "asc") {
      return <FaSortUp className="ms-1" aria-hidden="true" />;
    }

    return <FaSortDown className="ms-1" aria-hidden="true" />;
  };

  const stickyHeaderStyle = {
    position: "sticky",
    top: 0,
    zIndex: 1,
    backgroundColor: "var(--bs-table-bg, #f8f9fa)"
  };

  const getRowStyle = (index) => ({
    "--bs-table-bg": index % 2 === 0 ? "rgba(25, 135, 84, 0.06)" : "#ffffff"
  });

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3 gap-3 flex-wrap">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <div className="position-relative" style={{ width: "320px", maxWidth: "100%" }}>
            <input
              type="text"
              className="form-control pe-5"
              placeholder="Search devices..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            {search && (
              <button
                type="button"
                className="btn btn-sm position-absolute top-50 end-0 translate-middle-y me-2 border-0 bg-transparent text-muted"
                onClick={() => {
                  setSearch("");
                  setDebouncedSearch("");
                }}
                aria-label="Clear search"
              >
                x
              </button>
            )}
          </div>

          <span className="badge text-bg-secondary">
            Total: {filteredDevices.length}
          </span>
        </div>

        {canManage && (
          <button
            className="btn btn-success"
            onClick={() => {
              setEditingDevice(null);
              setShowForm(true);
            }}
          >
            Add device
          </button>
        )}
      </div>

      <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
        <table className="table table-hover align-middle mb-0">
          <thead className="table-light">
            <tr>
              <th
                style={{ ...stickyHeaderStyle, width: "260px", cursor: "pointer" }}
                onClick={() => handleSort("name")}
              >
                <span className="d-inline-flex align-items-center">
                  Name
                  {renderSortIndicator("name")}
                </span>
              </th>
              <th style={{ ...stickyHeaderStyle, minWidth: "360px" }}>Note</th>
              <th
                style={{ ...stickyHeaderStyle, width: "140px", cursor: "pointer" }}
                onClick={() => handleSort("user")}
              >
                <span className="d-inline-flex align-items-center">
                  User
                  {renderSortIndicator("user")}
                </span>
              </th>
              {canManage && <th style={{ ...stickyHeaderStyle, width: "120px" }}>Actions</th>}
            </tr>
          </thead>

          <tbody>
            {sortedDevices.length === 0 ? (
              <tr>
                <td colSpan={canManage ? 4 : 3} className="text-center text-muted py-4">
                  No devices found.
                </td>
              </tr>
            ) : sortedDevices.map((device, index) => (
              <tr key={device.id} style={getRowStyle(index)}>
                <td>
                  <button
                    className="btn btn-link p-0 fw-bold"
                    onClick={() => openDevice(device.id)}
                  >
                    {renderHighlightedText(device.name)}
                  </button>
                </td>
                <td>{renderHighlightedText(device.note)}</td>
                <td>{renderHighlightedText(device.User?.name)}</td>
                {canManage && (
                  <td>
                    <button
                      className="btn btn-sm btn-outline-primary me-2"
                      onClick={() => {
                        setEditingDevice(device);
                        setShowForm(true);
                      }}
                      title="Edit"
                    >
                      <FaEdit />
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => deleteDevice(device.id)}
                      title="Delete"
                    >
                      <FaTrash />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <DeviceModal
        show={showModal}
        onClose={() => setShowModal(false)}
        device={selectedDevice}
      />

      <DeviceFormModal
        show={showForm}
        onClose={() => setShowForm(false)}
        onSaved={loadDevices}
        device={editingDevice}
      />
    </div>
  );
}
